import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { createRisk } from '../../api/riskApi';
import { getRiskCates } from '../../api/riskCateApi';
import {
  STRENGTH_OPTIONS, FREQUENCY_OPTIONS,
  calcRiskLevel, getRiskLevelLabel, getRiskLevelClass,
} from '../../utils/riskUtils';
import OrgTreeSelect from '../../components/OrgSelect/OrgTreeSelect';
import ModalSchedule from '../schedule/ModalSchedule';
import '../schedule/ModalSchedule.css';
import '../schedule/Schedule.css';
import './risk.css';

const initForm = {
  risk_category_num: '',
  risk_category_context: '',
  risk_factors: '',
  risk_reduction_measures: '',
  current_risk_frequency: '',
  current_risk_strength: '',
  after_risk_frequency: '',
  after_risk_strength: '',
  request_date: '',
  complete_date: '',
};

function RiskForm() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [form, setForm] = useState(initForm);

  // 담당자 목록 (저장 포맷: { emp_no, name, position, department })
  const [chargeList, setChargeList] = useState([]);
  const [isChargeExpanded, setIsChargeExpanded] = useState(false);

  // 검색 관련
  const [searchTerm, setSearchTerm] = useState('');
  const [searchUsers, setSearchUsers] = useState([]);
  const [showSearchPopup, setShowSearchPopup] = useState(false);

  // 조직도 팝업
  const [showOrgPopup, setShowOrgPopup] = useState(false);

  // 분류
  const [riskCates, setRiskCates] = useState([]);
  const [firstCate, setFirstCate] = useState('');
  const [secondCate, setSecondCate] = useState('');

  // 이미지
  const [beforeImg, setBeforeImg] = useState(null);
  const [afterImg, setAfterImg] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getRiskCates(token)
      .then(data => setRiskCates(Array.isArray(data) ? data : []))
      .catch(err => console.error('분류 조회 실패:', err));
  }, [token]);

  const folderItems = riskCates.filter(c => c.category_type === 'folder');
  const childItems  = riskCates.filter(
    c => c.category_type === 'text' && Number(c.parent_id) === Number(firstCate)
  );

  /* ── 분류 핸들러 ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFirstCateChange = (e) => {
    setFirstCate(e.target.value);
    setSecondCate('');
    setForm(prev => ({ ...prev, risk_category_num: '', risk_category_context: '' }));
  };

  const handleSecondCateChange = (e) => {
    const val = e.target.value;
    setSecondCate(val);
    if (val) {
      const selected = riskCates.find(c => String(c.id) === String(val));
      if (selected) {
        setForm(prev => ({
          ...prev,
          risk_category_num:     selected.risk_category_num,
          risk_category_context: selected.risk_category_context,
        }));
      }
    } else {
      setForm(prev => ({ ...prev, risk_category_num: '', risk_category_context: '' }));
    }
  };

  /* ── 담당자 핸들러 ── */
  const addCharge = (u) => {
    const empNo = String(u.userid || u.id || '');
    setChargeList(prev => {
      if (prev.some(c => c.emp_no === empNo)) return prev;
      return [...prev, {
        emp_no:     empNo,
        name:       u.name       || '',
        position:   u.position   || '',
        department: u.dept_name  || '',
        checked:    false,
      }];
    });
  };

  const toggleCheck = (empNo) => {
    setChargeList(prev =>
      prev.map(c => c.emp_no === empNo ? { ...c, checked: !c.checked } : c)
    );
  };

  const deleteSelected = () => setChargeList(prev => prev.filter(c => !c.checked));
  const deleteAll      = () => setChargeList([]);

  /* ── 검색 ── */
  const handleSearch = (term) => {
    const keyword = (term || '').trim();
    if (!keyword) { alert('검색대상이 입력되지 않았습니다.'); return; }

    axios.get('/api/org/search', {
      params: { keyword },
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const users = (res.data.users || []);
      if (users.length === 0) { alert('검색 결과가 존재하지 않습니다.'); return; }
      if (users.length === 1) { addCharge(users[0]); setSearchTerm(''); return; }
      setSearchUsers(users.map(u => ({ ...u, checked: false })));
      setShowSearchPopup(true);
    }).catch(err => console.error('검색 실패:', err));
  };

  const toggleSearchSelect = (item) => {
    setSearchUsers(prev =>
      prev.map(u => u.userid === item.userid ? { ...u, checked: !u.checked } : u)
    );
  };

  const handleSearchConfirm = () => {
    searchUsers.filter(u => u.checked).forEach(u => addCharge(u));
    setSearchUsers([]);
    setShowSearchPopup(false);
    setSearchTerm('');
  };

  /* ── 조직도 ── */
  const handleOrgConfirm = (targets) => {
    const newList = targets.map(t => ({
      emp_no:     String(t.target_id   || ''),
      name:       t.target_name || '',
      position:   t.position    || '',
      department: t.dept_name   || '',
      checked:    false,
    }));
    setChargeList(newList);
    setShowOrgPopup(false);
  };

  /* ── 저장 ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!secondCate) { alert('2차 분류를 선택하세요.'); return; }

    setSubmitting(true);
    try {
      const currentLevel = calcRiskLevel(form.current_risk_frequency, form.current_risk_strength);
      const afterLevel   = calcRiskLevel(form.after_risk_frequency,   form.after_risk_strength);

      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => formData.append(key, val));
      if (currentLevel !== null) formData.append('current_risk_level', currentLevel);
      if (afterLevel   !== null) formData.append('after_risk_level',   afterLevel);

      // checked 필드 제외하고 저장
      const saveList = chargeList.map(({ checked, ...rest }) => rest);
      formData.append('charge_info', JSON.stringify(saveList));

      if (beforeImg) formData.append('before_img', beforeImg);
      if (afterImg)  formData.append('after_img',  afterImg);

      await createRisk(formData, token);
      navigate('/risk');
    } catch (err) {
      console.error('등록 실패:', err);
      alert('등록 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const dispName = (c) =>
    `${c.name}${c.position ? ' / ' + c.position : ''}${c.department ? ' (' + c.department + ')' : ''}`;

  return (
    <div className="container-fluid view-post-write">
      <div className="row">
        <div className="col-12">
          <h4 className="mb-4">위험성 평가 등록</h4>
          <form onSubmit={handleSubmit}>

            {/* 분류 정보 */}
            <div className="card mb-3">
              <div className="card-header fw-bold">분류 정보</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      1차 분류 <span className="text-danger">*</span>
                    </label>
                    <select className="form-select" value={firstCate} onChange={handleFirstCateChange}>
                      <option value="">선택하세요</option>
                      {folderItems.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.risk_category_num} - {item.risk_category_context}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      2차 분류 <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={secondCate}
                      onChange={handleSecondCateChange}
                      disabled={!firstCate}
                    >
                      <option value="">
                        {firstCate ? '선택하세요' : '1차 분류를 먼저 선택하세요'}
                      </option>
                      {childItems.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.risk_category_num} - {item.risk_category_context}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* 위험 요인 */}
            <div className="card mb-3">
              <div className="card-header fw-bold">위험 요인 및 대책</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">유해위험 요인</label>
                  <textarea className="form-control" name="risk_factors" rows={3}
                    value={form.risk_factors} onChange={handleChange} />
                </div>
                <div>
                  <label className="form-label fw-bold">감소 대책</label>
                  <textarea className="form-control" name="risk_reduction_measures" rows={3}
                    value={form.risk_reduction_measures} onChange={handleChange} />
                </div>
              </div>
            </div>

            {/* 위험성 평가 */}
            <div className="card mb-3">
              <div className="card-header fw-bold">위험성 평가</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12"><strong className="text-muted">현재 위험성</strong></div>
                  <div className="col-md-4">
                    <label className="form-label">빈도</label>
                    <select className="form-select" name="current_risk_frequency"
                      value={form.current_risk_frequency} onChange={handleChange}>
                      {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">강도</label>
                    <select className="form-select" name="current_risk_strength"
                      value={form.current_risk_strength} onChange={handleChange}>
                      {STRENGTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="text-muted small">위험성</label>
                    <div>
                      {(() => {
                        const lv = calcRiskLevel(form.current_risk_frequency, form.current_risk_strength);
                        return lv !== null
                          ? <span className={`badge bg-${getRiskLevelClass(lv)}`}>{lv} - {getRiskLevelLabel(lv)}</span>
                          : <span className="text-muted">-</span>;
                      })()}
                    </div>
                  </div>

                  <div className="col-12 mt-2"><strong className="text-muted">개선 후 위험성</strong></div>
                  <div className="col-md-4">
                    <label className="form-label">빈도</label>
                    <select className="form-select" name="after_risk_frequency"
                      value={form.after_risk_frequency} onChange={handleChange}>
                      {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">강도</label>
                    <select className="form-select" name="after_risk_strength"
                      value={form.after_risk_strength} onChange={handleChange}>
                      {STRENGTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="text-muted small">위험성</label>
                    <div>
                      {(() => {
                        const lv = calcRiskLevel(form.after_risk_frequency, form.after_risk_strength);
                        return lv !== null
                          ? <span className={`badge bg-${getRiskLevelClass(lv)}`}>{lv} - {getRiskLevelLabel(lv)}</span>
                          : <span className="text-muted">-</span>;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 담당자 */}
            <div className="card mb-3">
              <div className="card-header fw-bold">담당자 정보</div>
              <div className="card-body pb-3">
                {/* 검색 바 */}
                <div className="form-schedule-search-bar mb-2 d-flex">
                  <div className="form-schedule-search-left">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (searchTerm.trim()) handleSearch(searchTerm);
                        }
                      }}
                      placeholder="이름 검색"
                      className="form-control d-inline-block w-auto me-2"
                    />
                    <button
                      type="button"
                      className="btn btn-sm me-2"
                      style={{ backgroundColor: '#343a40', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px' }}
                      onClick={() => handleSearch(searchTerm)}
                    >
                      검색
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '4px 12px', borderRadius: '4px' }}
                      onClick={() => setShowOrgPopup(true)}
                    >
                      조직도 선택
                    </button>
                  </div>
                </div>

                {/* 담당자 리스트 */}
                <div className="form-schedule-content d-flex mb-0">
                  <div
                    className="form-schedule-list-box"
                    style={{
                      flex: '0 0 80%',
                      height: isChargeExpanded ? 'auto' : '115px',
                      overflowY: isChargeExpanded ? 'visible' : 'auto',
                    }}
                  >
                    {chargeList.length === 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6c757d', fontSize: '0.9rem' }}>
                        담당자가 없습니다.
                      </div>
                    ) : (
                      chargeList.map((c) => (
                        <label key={c.emp_no} className="d-block">
                          <input
                            type="checkbox"
                            checked={c.checked || false}
                            onChange={() => toggleCheck(c.emp_no)}
                            className="form-check-input me-2"
                          />
                          {dispName(c)}
                        </label>
                      ))
                    )}
                  </div>
                  <div className="form-schedule-delete-buttons d-flex flex-column align-items-start gap-2" style={{ flex: '1' }}>
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <div className="d-flex align-items-center">
                        {chargeList.length > 4 && (
                          <button
                            type="button"
                            className="btn-height"
                            onClick={() => setIsChargeExpanded(!isChargeExpanded)}
                          >
                            <i className={`fas ${isChargeExpanded ? 'fa-minus' : 'fa-plus'}`} />
                          </button>
                        )}
                      </div>
                      <div className="form-schedule-count fw-bold" style={{ whiteSpace: 'nowrap' }}>
                        Count : {chargeList.length}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={deleteSelected}>선택삭제</button>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={deleteAll}>전체삭제</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 일정 */}
            <div className="card mb-3">
              <div className="card-header fw-bold">일정</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">조치요구일</label>
                    <input type="date" className="form-control" name="request_date"
                      value={form.request_date} onChange={handleChange} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">완료일</label>
                    <input type="date" className="form-control" name="complete_date"
                      value={form.complete_date} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </div>

            {/* 이미지 */}
            <div className="card mb-4">
              <div className="card-header fw-bold">개선 이미지</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">개선 전 이미지</label>
                    <input type="file" className="form-control" accept="image/*"
                      onChange={e => setBeforeImg(e.target.files[0])} />
                    {beforeImg && (
                      <img src={URL.createObjectURL(beforeImg)} alt="개선 전"
                        className="img-fluid mt-2 rounded border" style={{ maxHeight: 200 }} />
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">개선 후 이미지</label>
                    <input type="file" className="form-control" accept="image/*"
                      onChange={e => setAfterImg(e.target.files[0])} />
                    {afterImg && (
                      <img src={URL.createObjectURL(afterImg)} alt="개선 후"
                        className="img-fluid mt-2 rounded border" style={{ maxHeight: 200 }} />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="risk-form-actions">
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? '등록 중...' : '등록 완료'}
              </button>
              <button type="button" className="btn btn-outline-primary" onClick={() => navigate('/risk')}>
                목록
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 검색 결과 모달 */}
      {showSearchPopup && (
        <ModalSchedule
          type="searchResult"
          data={{ users: searchUsers, depts: [] }}
          toggleSelect={toggleSearchSelect}
          onConfirm={handleSearchConfirm}
          onClose={() => { setShowSearchPopup(false); setSearchUsers([]); }}
        />
      )}

      {/* 조직도 팝업 */}
      {showOrgPopup && (
        <OrgTreeSelect
          selectionMode="multiple"
          targetType="user"
          initialSelected={chargeList.map(c => ({
            target_type: 'user',
            target_id:   c.emp_no,
            target_name: c.name,
            position:    c.position,
            dept_name:   c.department,
          }))}
          onConfirm={handleOrgConfirm}
          onCancel={() => setShowOrgPopup(false)}
        />
      )}
    </div>
  );
}

export default RiskForm;
